import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mui/material';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { formatEventDateRange } from '../../../shared/eventDate';
import { Card, Button, Modal, AnimatePresence, Skeleton, EmptyState, OutstandingBanner } from '../shared/UI';
import Badge, { LockBadge } from '../shared/Badge';
import PaymentPendingNote from '../shared/PaymentPendingNote';
import { TYPE_OPTIONS, SIZE_OPTIONS, FILTER_LEVELS, FILTER_STYLES } from '../../../shared/entryOptions';
import EntryFormFields, { FormRow, inputStyle, getRecommendedLevelIfBelow, getNonAttendingChoreographers } from '../../../shared/entryForm/EntryFormFields';
import LevelWarningModal from '../../../shared/entryForm/LevelWarningModal';
import ChoreographerLinkPanel from '../shared/ChoreographerLinkPanel';
import { useTitleBarSearch } from '../../context/TitleBarSearchContext';

const sectionHeaderStyle = { fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: 0.4, padding: '10px 0 6px' };

const SORT_OPTIONS = [
  { key: 'default', label: 'As entered' },
  { key: 'title', label: 'Title A–Z' },
  { key: 'level', label: 'Level' },
  { key: 'size', label: 'Size' },
];

// Full source docs, linked from the bottom of each help popup.
const DEFINITION_DOC_LINKS = {
  level: 'https://docs.google.com/document/d/10cf0I1lY9HXYiZhusx3DRQpI-eyncwnqU_GzC9ANpWM/edit?usp=sharing',
  size: 'https://docs.google.com/document/d/1J44WecYmYSIHgecyyGt5ABiA6LriPTfbJ_VikPvxqM8/edit?usp=sharing',
  style: 'https://docs.google.com/document/d/1oSLTMhVrMv_TEGJNu-8ryVF01-5Nw1cC4uWNFM2lV44/edit?tab=t.0',
};
const DEFINITION_LABELS = { level: 'Level', size: 'Size', style: 'Style' };

export default function EntryLibrary() {
  const { entries, dancers, groups, school, locks, loading, refetch } = useTeacherData();
  const [editingEntry, setEditingEntry] = useState(null); // null = list view, object = editing/new
  const isMobile = useMediaQuery('(max-width:768px)');

  // Filter/sort state — must be declared before the early returns below (rules of hooks)
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState(() => new Set());
  const [sizeFilter, setSizeFilter] = useState(() => new Set());
  const [sortKey, setSortKey] = useState('default');
  const [sortAsc, setSortAsc] = useState(true);
  const [linkPanelOpen, setLinkPanelOpen] = useState(false);

  // Below 768px the sidebar (and this search box's normal spot) isn't
  // shown — relocate it into the mobile title bar instead. No-op on
  // desktop, where the inline input below is used as always.
  useTitleBarSearch(isMobile ? { placeholder: 'Search entry, dancer, or choreographer', value: search, onChange: setSearch } : null);

  //turn on and off level chips
  const toggleLevel = (value) => setLevelFilter((prev) => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });

  //turn on and off size chips
  const toggleSize = (value) => setSizeFilter((prev) => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });

  const hasActiveFilters = search.trim() !== '' || styleFilter !== 'All' || levelFilter.size > 0 || sizeFilter.size > 0;

  //clears all filters
  const clearFilters = () => {
    setSearch('');
    setStyleFilter('All');
    setLevelFilter(new Set());
    setSizeFilter(new Set());
  };

  // Filter logic: AND across dimensions, OR within a chip row.
  // An empty chip set means "no filter on that dimension".
  const visibleEntries = useMemo(() => {
    let list = [...(entries || [])];

    const q = search.trim().toLowerCase();
    if (q) {
      const nameText = (ids) => (ids || [])
        .map((id) => (dancers || []).find((d) => d.id === id))
        .filter(Boolean)
        .map((d) => `${d.firstName} ${d.lastName}`)
        .join(' ');
      list = list.filter((e) =>
        `${e.title || ''} ${nameText(e.dancerIds)} ${nameText(e.choreographerIds)} ${e.nonAttendingChoreographers || ''}`.toLowerCase().includes(q)
      );
    }
    if (styleFilter !== 'All') list = list.filter((e) => e.style === styleFilter);
    if (levelFilter.size > 0) list = list.filter((e) => levelFilter.has(e.level));
    if (sizeFilter.size > 0) list = list.filter((e) => sizeFilter.has(e.size));

    if (sortKey !== 'default') {
      const rank = (arr, v) => {
        const i = arr.indexOf(v);
        return i === -1 ? arr.length : i;
      };
      list.sort((a, b) => {
        if (sortKey === 'title') return (a.title || '').localeCompare(b.title || '');
        if (sortKey === 'level') return rank(FILTER_LEVELS, a.level) - rank(FILTER_LEVELS, b.level);
        if (sortKey === 'size') return rank(SIZE_OPTIONS, a.size) - rank(SIZE_OPTIONS, b.size);
        return 0;
      });
    }
    if (!sortAsc) list.reverse();
    return list;
  }, [entries, dancers, search, styleFilter, levelFilter, sizeFilter, sortKey, sortAsc]);

  // Entries submitted via the choreographer link (see ChoreographerLinkPanel)
  // and not yet reviewed get their own section above the rest, rather than
  // just being sorted to the top of one flat list — a teacher shouldn't have
  // to scan the whole library to notice something needs their approval.
  const pendingEntries = useMemo(() => visibleEntries.filter((e) => e.status === 'Pending Review'), [visibleEntries]);
  const approvedEntries = useMemo(() => visibleEntries.filter((e) => e.status !== 'Pending Review'), [visibleEntries]);

  // Entries registered below the level their dancers' experience recommends
  // (same rule as the editor's dropdown warning). Uses all entries, not the
  // filtered list, so the banner doesn't change with search/filters.
  const levelWarnings = useMemo(() => {
    const map = new Map();
    (entries || []).forEach((e) => {
      const recommended = getRecommendedLevelIfBelow(e, dancers || []);
      if (recommended) map.set(e.id, recommended);
    });
    return map;
  }, [entries, dancers]);

  const bannerItems = useMemo(() => {
    const pendingItems = pendingEntries.map((e) => ({ label: `${e.title} — submitted by ${e.submittedByName || 'a choreographer'}` }));
    const levelItems = (entries || [])
      .filter((e) => levelWarnings.has(e.id))
      .map((e) => ({ label: `${e.title} — registered ${e.level}, recommended ${levelWarnings.get(e.id)}` }));
    if (pendingItems.length === 0) return levelItems;
    if (levelItems.length === 0) return pendingItems;
    return [
      { header: true, label: 'Pending your review' }, ...pendingItems,
      { header: true, label: 'Level below recommendation' }, ...levelItems,
    ];
  }, [pendingEntries, entries, levelWarnings]);

  const bannerTitle = levelWarnings.size === 0
    ? `${pendingEntries.length} ${pendingEntries.length === 1 ? 'entry' : 'entries'} pending your review`
    : pendingEntries.length === 0
      ? `${levelWarnings.size} ${levelWarnings.size === 1 ? 'entry is' : 'entries are'} below the recommended level`
      : `${pendingEntries.length + levelWarnings.size} items need your attention`;


  const startNewEntry = () => setEditingEntry({ id: null, title: '', type: 'Standard', style: 'Ballet', size: 'Solo', level: 'Novice', dancerIds: [], choreographerIds: [], props: false, propDescription: '', startPosition: '' });

  const openEntry = (entry) => {
    const inferredType = entry.type ||
      (entry.level === 'Freestyle' ? 'Freestyle'
        : (entry.style === 'Creative Collaboration') ? 'Creative Collaboration'
          : 'Standard');
    // entry.props is the raw sheet value — the freeform description text
    // itself (see the onSave note below), so the checkbox is derived from
    // whether there's any text, and the textarea is seeded from that same text.
    setEditingEntry({ ...entry, type: inferredType, props: !!entry.props, propDescription: entry.props || '' });
  };

  // Skip the loading skeleton while an entry is open for editing — a music
  // upload/remove triggers refetch.entries() in the background (onMusicChanged),
  // which briefly flips loading.entries back to true. Without this guard that
  // unmounts EntryEditor mid-edit (wiping its freshly-updated local state) and
  // remounts it from the stale `editingEntry` snapshot once the fetch finishes.
  if (loading.entries && !editingEntry) {
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 12 }}>Entry library</h1>
        <Card>
          <Skeleton height={38} radius={8} style={{ marginBottom: 14 }} />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ padding: '9px 0', borderBottom: '1px solid #E5E5E5' }}>
              <Skeleton width="40%" height={16} style={{ marginBottom: 8 }} />
              <Skeleton width="60%" height={12} />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  //entry library view
  if (!editingEntry) {
    // Different reasons new entries can be closed — a waitlisted school has
    // no accepted seat at the event at all yet, a withdrawn school had one
    // but isn't attending anymore (see getSchoolBlockStatus on the backend),
    // separate from entriesLocked (deadline passed, an admin/teacher-
    // requested invoice lock, or hitting the school's own entry estimate —
    // see entriesAtEstimate). All block "+ New entry" the same way; the
    // banner text tells them which one applies.
    const newEntriesBlocked = locks?.entriesLocked || locks?.waitlisted || locks?.withdrawn || locks?.notRegistered;
    return (
      <div style={{ padding: 20, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Entry library</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* No choreographer link until the school has registered for the event. */}
            {!locks?.notRegistered && (
              <Button size="sm" onClick={() => setLinkPanelOpen(true)}>Get choreographer submission link</Button>
            )}
            {!newEntriesBlocked && (
              <Button variant="primary" size="sm" onClick={startNewEntry}>
                + New entry
              </Button>
            )}
          </div>
        </div>
        {linkPanelOpen && <ChoreographerLinkPanel onClose={() => setLinkPanelOpen(false)} />}
        {bannerItems.length > 0 && (
          <OutstandingBanner id="entries-outstanding" title={bannerTitle} items={bannerItems} />
        )}
        <PaymentPendingNote locks={locks} what="entries" />
        {newEntriesBlocked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <LockBadge locks={locks} />
            <span style={{ fontSize: 13, color: '#666' }}>
              {locks?.notRegistered
                ? "New entries can't be added yet — your school hasn't registered for this event. Register for the event first."
                : locks?.waitlisted
                ? "New entries are closed — your school is on the event waitlist. You'll be able to add entries once you're promoted off the waitlist."
                : locks?.withdrawn
                  ? "New entries are closed — your school has withdrawn from this event."
                  : locks?.entriesAtEstimate
                    ? "New entries are closed — you've reached the estimated number of entries you registered with. Contact OSSDF if you need to raise your estimate. You can still edit existing entries, including swapping dancers, up to each entry's current headcount."
                    : "New entries are closed — the entries invoice has been requested. You can still edit existing entries, including swapping dancers, up to each entry's current headcount."}
            </span>
          </div>
        )}
        <Card>
          {/* search, sort and filter controls */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {!isMobile && (
                <input
                  style={{ ...inputStyle, flex: 1, minWidth: 10 }}
                  placeholder="Search entry, dancer, or choreographer"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
              <select
                style={{ ...inputStyle, width: 250 }}
                value={styleFilter}
                onChange={(e) => setStyleFilter(e.target.value)}
              >
                <option value="All">All styles</option>
                {FILTER_STYLES.map((s) => (
                  <option key={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <FilterChipRow label="Level" options={FILTER_LEVELS} selected={levelFilter} onToggle={toggleLevel} />
            <FilterChipRow label="Size" options={SIZE_OPTIONS} selected={sizeFilter} onToggle={toggleSize} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#666' }}>
                  Showing {visibleEntries.length} of {(entries || []).length} entries
                </span>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 12, color: '#2D2E33', cursor: 'pointer' }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#999' }}>Sort</span>
                <select
                  style={{ ...inputStyle, width: 130, padding: '5px 8px', fontSize: 13 }}
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <button
                  onClick={() => setSortAsc((a) => !a)}
                  title={sortAsc ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                  style={{ ...inputStyle, width: 34, padding: '5px 0', fontSize: 14, cursor: 'pointer', textAlign: 'center', background: '#fff' }}
                >
                  {sortAsc ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>

          {/* Table with all the entries */}
          {(entries || []).length === 0 ? (
            <EmptyState
              icon="🩰"
              title="No entries yet"
              description="Create your first dance entry to start building your school's library."
              action={!newEntriesBlocked && <Button variant="primary" onClick={startNewEntry}>+ New entry</Button>}
            />
          ) : visibleEntries.length === 0 ? (
            <EmptyState
              icon="🔍"
              title="No entries match these filters"
              description="Try adjusting or clearing your search and filters."
              action={<Button onClick={clearFilters}>Clear filters</Button>}
            />
          ) : (
            <>
              {pendingEntries.length > 0 && (
                <>
                  <div style={sectionHeaderStyle}>Pending review ({pendingEntries.length})</div>
                  {pendingEntries.map((entry) => (
                    <EntryRow key={entry.id} entry={entry} dancers={dancers || []} recommendedLevel={levelWarnings.get(entry.id)} onEdit={() => openEntry(entry)} />
                  ))}
                  {approvedEntries.length > 0 && <div style={sectionHeaderStyle}>All entries</div>}
                </>
              )}
              {approvedEntries.map((entry) => (
                <EntryRow key={entry.id} entry={entry} dancers={dancers || []} recommendedLevel={levelWarnings.get(entry.id)} onEdit={() => openEntry(entry)} />
              ))}
            </>
          )}
        </Card >
      </div >
    );
  }

  //editing mode screen
  return (
    <EntryEditor
      entry={editingEntry}
      dancers={dancers || []}
      groups={groups || []}
      onCancel={() => setEditingEntry(null)}
      onSave={async (entry) => {
        try {
          if (entry.id) {
            await sheetsClient.saveEntry({
              originalTitle: entry.id,
              schoolName: school?.name || '',
              title: entry.title,
              level: entry.level,
              style: entry.style,
              size: entry.size,
              numOfDancers: (entry.dancerIds || []).length,
              choreographerIds: entry.choreographerIds || [],
              nonAttendingChoreographers: getNonAttendingChoreographers(entry),
              dancerIds: entry.dancerIds || [],
              originalChoreographers: entry.style === 'K-Pop' ? (entry.originalChoreographers || '') : '',
              // The sheet only has one freeform "prop" column — the checkbox is
              // just a local UI toggle for showing the textarea, so what's
              // actually persisted is the description text itself.
              props: entry.propDescription || '',
              startPosition: entry.startPosition || '',
            });
            await refetch.entries();
            setEditingEntry(null);
          } else {
            await sheetsClient.createEntry({
              schoolName: school?.name || '',
              title: entry.title,
              level: entry.level,
              style: entry.style,
              size: entry.size,
              numOfDancers: (entry.dancerIds || []).length,
              choreographerIds: entry.choreographerIds || [],
              nonAttendingChoreographers: getNonAttendingChoreographers(entry),
              dancerIds: entry.dancerIds || [],
              originalChoreographers: entry.style === 'K-Pop' ? (entry.originalChoreographers || '') : '',
              acknowledgement: true,
              // The sheet only has one freeform "prop" column — the checkbox is
              // just a local UI toggle for showing the textarea, so what's
              // actually persisted is the description text itself.
              props: entry.propDescription || '',
              startPosition: entry.startPosition || '',
            });

            // Music was picked before the entry existed (see MusicUploadControl's
            // "pending" state) — the entry just got created, so upload it now as
            // a second step of this same "Create entry" click, not a separate
            // teacher-visible action.
            if (entry.pendingMusicFile) {
              try {
                const { uploadUrl, key } = await sheetsClient.getMusicUploadUrl(entry.title, entry.pendingMusicFile.type || 'audio/mpeg');
                const putRes = await fetch(uploadUrl, {
                  method: 'PUT',
                  body: entry.pendingMusicFile,
                  headers: { 'Content-Type': entry.pendingMusicFile.type || 'audio/mpeg' },
                });
                if (!putRes.ok) throw new Error('Upload to storage failed');
                await sheetsClient.confirmMusicUpload(entry.title, key);
              } catch (musicErr) {
                // Don't discard the entry that *did* save — keep the editor
                // open in edit mode so retrying doesn't mean redoing the form.
                await refetch.entries();
                setEditingEntry({ ...entry, id: entry.title, pendingMusicFile: null });
                alert(`Entry saved, but the music upload failed: ${musicErr.message}. You can try uploading it again below.`);
                return;
              }
            }

            await refetch.entries();
            setEditingEntry(null);
          }
        } catch (err) {
          alert(err.message);
        }
      }}
      onMusicChanged={refetch.entries}
      onDelete={editingEntry.id ? async () => {
        if (!window.confirm(`Delete "${editingEntry.title}"? This cannot be undone.`)) return;
        await sheetsClient.deleteEntry(editingEntry.id);
        await refetch.entries();
        setEditingEntry(null);
      } : undefined}
    />
  );
}

function EntryRow({ entry, dancers, recommendedLevel, onEdit }) {
  const dancerNames = (entry.dancerIds || [])
    .map((id) => dancers.find((d) => d.id === id)).filter(Boolean).map((d) => `${d.firstName} ${d.lastName[0]}.`).join(', ');

  const choreographerNames = [
    ...(entry.choreographerIds || [])
      .map((id) => dancers.find((d) => d.id === id)).filter(Boolean).map((d) => `${d.firstName} ${d.lastName[0]}.`),
    entry.nonAttendingChoreographers ? `${entry.nonAttendingChoreographers} (not attending)` : null,
  ].filter(Boolean).join(', ');

  return (
    <div onClick={onEdit} style={{ padding: '9px 0', borderBottom: '1px solid #E5E5E5', cursor: 'pointer' }}>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{entry.title}
        <span style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
          {[' ',
            entry.level,
            entry.style && entry.style !== entry.type ? entry.style : null,
            entry.size && entry.size !== entry.type ? entry.size : null,
          ].filter(Boolean).join(' · ')}
          {!entry.music && entry.level !== 'Freestyle' && <Badge variant="warning">Music needed</Badge>}
          {entry.status === 'Pending Review' && <Badge variant="warning">Pending review</Badge>}
          {recommendedLevel && <Badge variant="warning">Below recommended level ({recommendedLevel})</Badge>}
        </span>
      </div>
      {entry.status === 'Pending Review' && (
        <span style={{ display: 'block', fontSize: 12, color: '#854F0B', marginTop: 2 }}>
          Submitted by {entry.submittedByName || 'a choreographer'} — review and approve below.
        </span>
      )}
      {choreographerNames && (
        <span style={{ display: 'block', fontSize: 12, color: '#666', marginTop: 2 }}>
          Choreographer(s): {choreographerNames}
          {entry.originalChoreographers && ` — original choreography by ${entry.originalChoreographers}`}
        </span>
      )}
      {dancerNames && <span style={{ display: 'block', fontSize: 12, color: '#666', marginTop: 2 }}>Dancer(s): {dancerNames}
      </span>}
    </div>
  );
}

function EntryEditor({ entry, dancers, groups, onCancel, onSave, onDelete, onMusicChanged }) {
  const { event } = useTeacherData();
  const currEvent = event?.[0];
  const eventName = currEvent?.eventName || 'OSSDF';
  const eventDateRange = formatEventDateRange(currEvent?.startDate, currEvent?.endDate);
  const normalizedType = TYPE_OPTIONS.includes(entry.type) ? entry.type : 'Standard';
  const [form, setForm] = useState({
    ...entry,
    type: normalizedType,
    title: entry.title ?? '',
    style: entry.style ?? 'Ballet',
    size: entry.size ?? 'Solo',
    level: entry.level ?? 'Novice',
  });
  const [helpField, setHelpField] = useState(null); // null | 'level' | 'size' | 'style'
  const [submitting, setSubmitting] = useState(false);
  const [levelWarning, setLevelWarning] = useState(null); // null | recommended level name

  // Music upload state — separate from `form` since a picked-but-not-yet-saved
  // file (for a brand-new entry) is transient UI state, not a sheet field.
  const [pendingMusicFile, setPendingMusicFile] = useState(null);
  const [musicBusy, setMusicBusy] = useState(false);
  const [musicError, setMusicError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isFreestyle = form.type === 'Freestyle';
  const isCreativeCollab = form.type === 'Creative Collaboration';
  const isPending = form.status === 'Pending Review';

  const MAX_MUSIC_BYTES = 25 * 1024 * 1024;

  const validateMusicFile = (file) => {
    const isMp3 = file.type === 'audio/mpeg' || /\.mp3$/i.test(file.name);
    if (!isMp3) return 'Only MP3 files are accepted.';
    if (file.size > MAX_MUSIC_BYTES) return 'File is too large — max 25MB.';
    return null;
  };

  // Uploads straight to R2 via a presigned URL, then records the resulting
  // key against the entry. Only reachable once the entry already has an id.
  const uploadMusicFile = async (file) => {
    const { uploadUrl, key } = await sheetsClient.getMusicUploadUrl(form.id, file.type || 'audio/mpeg');
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'audio/mpeg' },
    });
    if (!putRes.ok) throw new Error('Upload to storage failed');
    await sheetsClient.confirmMusicUpload(form.id, key);
    setForm((f) => ({ ...f, music: key }));
    onMusicChanged?.();
  };

  const handleMusicFileSelected = async (file) => {
    setMusicError('');
    setPreviewUrl(null);
    const invalidReason = validateMusicFile(file);
    if (invalidReason) { setMusicError(invalidReason); return; }

    if (!form.id) {
      // No row to attach to yet — stash it and it goes up with the entry
      // when "Create entry"/"Submit entry" is clicked (see EntryLibrary's onSave).
      setPendingMusicFile(file);
      return;
    }

    setMusicBusy(true);
    try {
      await uploadMusicFile(file);
    } catch (err) {
      setMusicError(err.message);
    } finally {
      setMusicBusy(false);
    }
  };

  const handleRemoveMusic = async () => {
    setMusicError('');
    setPreviewUrl(null);
    if (!form.id) {
      setPendingMusicFile(null);
      return;
    }
    setMusicBusy(true);
    try {
      await sheetsClient.deleteMusic(form.id);
      setForm((f) => ({ ...f, music: '' }));
      onMusicChanged?.();
    } catch (err) {
      setMusicError(err.message);
    } finally {
      setMusicBusy(false);
    }
  };

  const handlePreviewMusic = async () => {
    setMusicError('');
    setPreviewLoading(true);
    try {
      const { url } = await sheetsClient.getMusicPreviewUrl(form.id);
      setPreviewUrl(url);
    } catch (err) {
      setMusicError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Gate on the same below-recommended-level rule the Level dropdown warns
  // about — the entry can't be saved until the level is raised.
  const handleSaveClick = () => {
    const recommendedLevel = getRecommendedLevelIfBelow(form, dancers);
    if (recommendedLevel) {
      setLevelWarning(recommendedLevel);
      return;
    }
    handleSave();
  };

  const handleSave = async () => {
    setLevelWarning(null);
    const savedForm = { ...form, pendingMusicFile };
    if (isFreestyle) {
      savedForm.size = 'Solo';
      savedForm.level = 'Freestyle';
    } else if (isCreativeCollab) {
      savedForm.style = 'Creative Collaboration';
    }
    setSubmitting(true);
    try {
      await onSave(savedForm);
    } finally {
      setSubmitting(false);
    }
  };

  // Note: the trailing type-specific sentence was previously unreachable —
  // `str + str + isFreestyle ? a : b` parses as `(str + str + isFreestyle) ?
  // a : b`, and string-plus-boolean concatenation is always truthy, so this
  // always evaluated to the isFreestyle branch regardless of its actual
  // value and the intro paragraph never rendered. Parenthesized below to fix
  // both that and the hardcoded event name/dates in one pass.
  const welcomeMessage = `OSSDF is excited to share this dance entry with like-minded students from all across Ontario at our event, ${eventName}${eventDateRange ? `, from ${eventDateRange}` : ''}.\n\n` +
    "Each dance requires its own registration entry, indicating the specific details for each dance. For additional entry definitions, please visit ossdancefest.ca.\n\n" +
    (isFreestyle ? 'Freestyle entries are solo performances. The entry title will be auto-filled from the dancer name.' : isCreativeCollab ? 'Creative Collaboration entries should be a collaborative effort. Please select all choreographers and participants from your roster.' : '');

  return (
    <div style={{ padding: 20, maxWidth: 780, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Button size="sm" onClick={onCancel}>← Back to library</Button>
      </div>
      <Card title={form.id ? `Edit entry — ${form.title}` : 'Add new entry'} subtitle={form.id ? undefined : welcomeMessage}>
        {isPending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Badge variant="warning">Pending review</Badge>
            <span style={{ fontSize: 13, color: '#666' }}>
              Submitted by {form.submittedByName || 'a choreographer'} via your submission link — review the fields below, edit anything as needed, then approve.
            </span>
          </div>
        )}

        <EntryFormFields form={form} setForm={setForm} dancers={dancers} groups={groups} onHelp={setHelpField} />

        <AnimatePresence>
          {helpField && <DefinitionsModal key="definitions-modal" field={helpField} onClose={() => setHelpField(null)} />}
          {levelWarning && (
            <LevelWarningModal
              key="level-warning-modal"
              level={form.level}
              recommendedLevel={levelWarning}
              onClose={() => setLevelWarning(null)}
            />
          )}
        </AnimatePresence>

        {!isFreestyle && (
          <FormRow label="Music file">
            <MusicUploadControl
              musicKey={form.music}
              pendingFile={pendingMusicFile}
              busy={musicBusy}
              error={musicError}
              previewUrl={previewUrl}
              previewLoading={previewLoading}
              onFileSelected={handleMusicFileSelected}
              onRemove={handleRemoveMusic}
              onPreview={handlePreviewMusic}
            />
          </FormRow>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          {form.id ? (
            <Button variant="primary" onClick={handleSaveClick} disabled={submitting}>{submitting ? 'Saving…' : isPending ? '✓ Approve & save' : '✓ Save entry'}</Button>
          ) :
            (
              <Button
                variant="primary"
                onClick={handleSaveClick}
                disabled={submitting || !form.acknowledgement || !form.title || (form.choreographerIds.length === 0 && !getNonAttendingChoreographers(form)) || !form.dancerIds || (!isFreestyle && !form.startPosition) || (form.style === 'K-Pop' && !form.originalChoreographers?.trim())}
              >
                {submitting ? 'Saving…' : '✓ Submit entry'}
              </Button>
            )}

          <Button onClick={onCancel} disabled={submitting}>Cancel</Button>

          {onDelete && (
            <div style={{ marginLeft: 'auto' }}>
              <Button variant="danger" onClick={onDelete} disabled={submitting}>🗑 Delete entry</Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function MusicUploadControl({ musicKey, pendingFile, busy, error, previewUrl, previewLoading, onFileSelected, onRemove, onPreview }) {
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file (e.g. after fixing it)
    if (file) onFileSelected(file);
  };

  const hasMusic = !!musicKey;
  const hasPending = !!pendingFile;

  return (
    <div>
      <input ref={fileInputRef} type="file" accept=".mp3,audio/mpeg" onChange={handleChange} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {hasMusic ? (
          <>
            <span style={{ fontSize: 13, color: '#1A1A1A' }}>🎵 Music attached</span>
            <Button size="sm" onClick={onPreview} disabled={busy || previewLoading}>{previewLoading ? '…' : '▶ Preview'}</Button>
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={busy}>Replace</Button>
            <Button size="sm" onClick={onRemove} disabled={busy}>{busy ? '…' : 'Remove'}</Button>
          </>
        ) : hasPending ? (
          <>
            <span style={{ fontSize: 13, color: '#1A1A1A' }}>
              🎵 {pendingFile.name} <span style={{ color: '#666' }}>(will upload when you save)</span>
            </span>
            <Button size="sm" onClick={() => fileInputRef.current?.click()}>Choose different file</Button>
            <Button size="sm" onClick={onRemove}>Remove</Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={busy}>{busy ? 'Uploading…' : '↑ Upload music file'}</Button>
            <span style={{ fontSize: 13, color: '#666' }}>No file yet</span>
          </>
        )}
      </div>
      {previewUrl && (
        <audio controls autoPlay src={previewUrl} style={{ marginTop: 8, width: '100%', maxWidth: 360, display: 'block' }} />
      )}
      {error && <div style={{ fontSize: 12, color: '#C0392B', marginTop: 6 }}>{error}</div>}
      <span style={{ display: 'block', fontSize: 13, fontStyle: 'italic', color: '#666', marginTop: 6 }}>MP3 files only, up to 25MB.</span>
    </div>
  );
}

// Scoped styling for the raw HTML pulled from the Google Doc export — Google's
// own inline styles/classes are stripped server-side, so this is the only
// styling that content gets.
const DOC_BODY_CSS = `
  .doc-body { font-size: 13px; color: #1A1A1A; line-height: 1.5; }
  .doc-body p { margin: 0 0 8px; }
  .doc-body h1, .doc-body h2, .doc-body h3 { margin: 16px 0 6px; font-weight: 600; }
  .doc-body h1:first-child, .doc-body h2:first-child, .doc-body h3:first-child { margin-top: 0; }
  .doc-body ul, .doc-body ol { margin: 0 0 10px 20px; padding: 0; }
  .doc-body li { margin-bottom: 3px; }
  .doc-body a { color: #2D2E33; }
  .doc-body table { border-collapse: collapse; width: 100%; margin: 8px 0 14px; }
  .doc-body td, .doc-body th { border: 1px solid #D5D5D5; padding: 6px 8px; text-align: left; vertical-align: top; }
  .doc-body th { background: #F5F5F5; font-weight: 600; }
`;

function DefinitionsModal({ field, onClose }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    sheetsClient.getFieldDefinition(field)
      .then((res) => { if (!cancelled) setHtml(res.html); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [field]);

  return (
    <Modal onClose={onClose} panelStyle={{ width: 620, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <style>{DOC_BODY_CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #E5E5E5' }}>
        <span style={{ fontSize: 15, fontWeight: 500 }}>{DEFINITION_LABELS[field]} definitions</span>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#666' }}>✕</button>
      </div>
      <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
        {error ? (
          <span style={{ fontSize: 13, color: '#A32D2D' }}>Couldn't load definitions: {error}</span>
        ) : html === null ? (
          <span style={{ fontSize: 13, color: '#666' }}>Loading…</span>
        ) : (
          <div className="doc-body" dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </div>
      <div style={{ padding: '10px 18px', borderTop: '1px solid #E5E5E5' }}>
        <a href={DEFINITION_DOC_LINKS[field]} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#2D2E33' }}>
          Open full document ↗
        </a>
      </div>
    </Modal>
  );
}

function FilterChipRow({ label, options, selected, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 12, color: '#999', width: 42, flexShrink: 0 }}>{label}</span>
      {options.map((opt) => {
        const active = selected.has(opt);
        return (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            style={{
              fontSize: 13,
              padding: '4px 12px',
              borderRadius: 999,
              cursor: 'pointer',
              border: `1px solid ${active ? '#ED1C24' : '#D5D5D5'}`,
              background: active ? '#FDEDEB' : '#fff',
              color: active ? '#ED1C24' : '#666',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
